import { ref, Ref } from 'vue';
import DiscountsService from '@/api/services/discounts.service';
import { IDiscount } from '@/interfaces/IDiscount';
import { IOrder } from '@/interfaces/IOrder';
import { DiscountType } from '@/enums/DiscountType';
import { AlertType } from '@/interfaces/IAlert';
import { useUi } from './use-ui';
import { useOrders } from './use-orders';

export function useDiscounts() {
  const ui = useUi();
  const ordersHelper = useOrders();
  const loading = ref(false);

  async function listDiscounts(orderId: number): Promise<IDiscount[]> {
    try {
      loading.value = true;
      return await DiscountsService.list(orderId);
    } catch (error) {
      ui.serverError(error, 'Error al cargar descuentos');
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function createDiscount(
    order: Ref<IOrder>,
    data: {
      type: DiscountType;
      isPercentage: boolean;
      value: number;
      referenceId?: number | null;
      metadata?: Record<string, any> | null;
    }
  ): Promise<IDiscount | null> {
    try {
      loading.value = true;

      const discount = await DiscountsService.create({
        orderId: order.value.id,
        ...data,
      });

      // Refresh the order to get updated totals
      await ordersHelper.refreshOrder({ order: order.value });

      ui.showToast(AlertType.Success, 'Descuento aplicado');
      return discount;
    } catch (error) {
      ui.serverError(error, 'Error al crear descuento');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateDiscount(
    order: Ref<IOrder>,
    discountId: number,
    data: {
      isPercentage?: boolean;
      value?: number;
      metadata?: Record<string, any> | null;
    }
  ): Promise<IDiscount | null> {
    try {
      loading.value = true;

      const discount = await DiscountsService.update(
        order.value.id,
        discountId,
        data
      );

      // Refresh the order to get updated totals
      await ordersHelper.refreshOrder({ order: order.value });

      ui.showToast(AlertType.Success, 'Descuento actualizado');
      return discount;
    } catch (error) {
      ui.serverError(error, 'Error al actualizar descuento');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function deleteDiscount(
    order: Ref<IOrder>,
    discountId: number
  ): Promise<boolean> {
    try {
      loading.value = true;

      await DiscountsService.delete(order.value.id, discountId);

      // Refresh the order to get updated totals
      await ordersHelper.refreshOrder({ order: order.value });

      ui.showToast(AlertType.Success, 'Descuento eliminado');
      return true;
    } catch (error) {
      ui.serverError(error, 'Error al eliminar descuento');
      return false;
    } finally {
      loading.value = false;
    }
  }

  function formatDiscountLabel(discount: IDiscount): string {
    switch (discount.type) {
      case DiscountType.Manual:
        // Table-discount shares are ordinary manual rows tagged by the API.
        return discount.metadata?.source === 'table' ? 'Mesa' : 'Manual';
      case DiscountType.Coupon:
        return discount.metadata?.couponCode || 'Cupón';
      case DiscountType.Rule:
        return discount.metadata?.ruleName || 'Regla';
      case DiscountType.Club:
        return discount.metadata?.club?.points
          ? `Puntos Club (${discount.metadata.club.points} pts)`
          : 'Puntos Club';
      default:
        return 'Descuento';
    }
  }

  function formatDiscountValue(
    discount: IDiscount,
    currencySymbol: string
  ): string {
    if (discount.isPercentage) {
      return `${discount.value}%`;
    } else {
      return `${currencySymbol}${discount.value.toFixed(2)}`;
    }
  }

  return {
    loading,
    listDiscounts,
    createDiscount,
    updateDiscount,
    deleteDiscount,
    formatDiscountLabel,
    formatDiscountValue,
  };
}
